import type { ReactNode } from 'react';
import { CircleAlert, Info, OctagonAlert } from 'lucide-react';
import { cn } from '../lib/cn.js';

export type CalloutTone = 'info' | 'warn' | 'critical';

export interface CalloutProps {
  tone?: CalloutTone;
  title?: string;
  children: ReactNode;
  /** Optional action row (buttons) under the body. */
  actions?: ReactNode;
  className?: string;
}

const TONE: Record<CalloutTone, { bar: string; text: string; Icon: typeof Info; role: 'status' | 'alert' }> = {
  info: { bar: 'bg-info', text: 'text-info', Icon: Info, role: 'status' },
  warn: { bar: 'bg-warn', text: 'text-warn', Icon: CircleAlert, role: 'status' },
  critical: { bar: 'bg-brand', text: 'text-brand-text', Icon: OctagonAlert, role: 'alert' },
};

/** Bordered note with a 2px tone bar on the left edge. Never a full tone fill (section 3.5). */
export function Callout({ tone = 'info', title, children, actions, className }: CalloutProps) {
  const t = TONE[tone];
  return (
    <div role={t.role} className={cn('relative rounded-sm border border-line bg-panel px-4 py-3 pl-5', className)}>
      <span aria-hidden className={cn('absolute inset-y-0 left-0 w-[2px]', t.bar)} />
      <div className="flex items-start gap-2.5">
        <t.Icon size={13} className={cn('mt-0.5 shrink-0', t.text)} aria-hidden />
        <div className="min-w-0 flex-1">
          {title ? <p className={cn('text-nav font-medium tracking-tight', t.text)}>{title}</p> : null}
          <div className={cn('text-nav leading-snug text-ink/85', title ? 'mt-1' : '')}>{children}</div>
          {actions ? <div className="mt-3 flex flex-wrap gap-2">{actions}</div> : null}
        </div>
      </div>
    </div>
  );
}
